/**
 * The Home page's layout rule (Home spec 2026-09-23 D3, D5), pure. A layout
 * is an ordered list of widget ids with a width each; the stored preference
 * is resolved over the registry (core + module entries), the page draws what
 * the viewer may see, and every Customize action is a reducer here. The lanes
 * (wide cards left, narrow right, between full-width cards) are derived at
 * draw from the list, never stored.
 */
import type { HomeWidgetEntry, HomeWidth } from '@/components/custom/homeWidgetTypes'

export interface HomeLayoutEntry { id: string; width: HomeWidth }
export type LayoutWidget = Pick<HomeWidgetEntry, 'id' | 'width' | 'candidate'>

export const WIDTHS: HomeWidth[] = [1, 2, 3]

/** The initial layout (Home spec D5 as amended by SA-1178; design pass spec
 * §3.1, R6): the operation strip, the tiles and the briefing full width, then
 * the lists in decision 51's order of attention — work, customers, money. Ids
 * the registry does not know (a deployment without the fleet or compass
 * module) are skipped, and a widget the viewer may not see is kept here and
 * skipped at draw (D3): below the list frame dense flow closes the hole, and at it
 * the columns do. A stored layout is never
 * rewritten (R8): this list is read only when `ui.home.widgets` is null. */
export const INITIAL_LAYOUT: HomeLayoutEntry[] = [
  { id: 'operation-strip', width: 3 },
  { id: 'at-a-glance', width: 3 },
  { id: 'briefing', width: 3 },
  { id: 'my-work', width: 2 }, { id: 'new-enquiries', width: 1 },
  { id: 'with-customers', width: 2 }, { id: 'open-quotes', width: 1 },
  { id: 'decisions-pending', width: 2 }, { id: 'block-hours', width: 1 },
  { id: 'demand-matches', width: 2 }, { id: 'closed-this-month', width: 1 },
]

export function clampWidth(widget: LayoutWidget, width: HomeWidth): HomeWidth {
  const allowed = widget.width.allowed ?? WIDTHS
  return allowed.includes(width) ? width : widget.width.default
}

export function initialLayout(widgets: LayoutWidget[]): HomeLayoutEntry[] {
  const byId = new Map(widgets.map((x) => [x.id, x]))
  const listed = INITIAL_LAYOUT.flatMap((e) => {
    const widget = byId.get(e.id)
    return widget ? [{ id: e.id, width: clampWidth(widget, e.width) }] : []
  })
  const rest = widgets
    .filter((x) => !x.candidate && !INITIAL_LAYOUT.some((e) => e.id === x.id))
    .map((x) => ({ id: x.id, width: x.width.default }))
  return [...listed, ...rest]
}

/** The stored list over the registry: unknown ids and duplicates dropped,
 * widths clamped. An inaccessible id is NOT dropped here — access is the
 * page's concern at draw time, so the preference keeps it (spec D3). The
 * preference is a stored blob, so it is read defensively: anything that is
 * not a list reads as no preference (the initial layout), and an entry that
 * is not an object with a string id is skipped — never a throw. */
export function resolveLayout(pref: unknown, widgets: LayoutWidget[]): HomeLayoutEntry[] {
  if (!Array.isArray(pref)) return initialLayout(widgets)
  const byId = new Map(widgets.map((x) => [x.id, x]))
  const seen = new Set<string>()
  const out: HomeLayoutEntry[] = []
  for (const e of pref as unknown[]) {
    if (typeof e !== 'object' || e === null) continue
    const { id, width } = e as { id?: unknown; width?: unknown }
    if (typeof id !== 'string') continue
    const widget = byId.get(id)
    if (!widget || seen.has(id)) continue
    seen.add(id)
    out.push({ id, width: clampWidth(widget, width as HomeWidth) })
  }
  return out
}

export function moveWidget(layout: HomeLayoutEntry[], from: number, to: number): HomeLayoutEntry[] {
  if (from === to || from < 0 || to < 0 || from >= layout.length || to >= layout.length) return layout
  const next = [...layout]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

export function setWidgetWidth(layout: HomeLayoutEntry[], id: string, width: HomeWidth, widgets: LayoutWidget[]): HomeLayoutEntry[] {
  const widget = widgets.find((x) => x.id === id)
  if (!widget) return layout
  return layout.map((e) => (e.id === id ? { id, width: clampWidth(widget, width) } : e))
}

export function removeWidget(layout: HomeLayoutEntry[], id: string): HomeLayoutEntry[] {
  return layout.filter((e) => e.id !== id)
}

export function addWidget(layout: HomeLayoutEntry[], widget: LayoutWidget): HomeLayoutEntry[] {
  if (layout.some((e) => e.id === widget.id)) return layout
  return [...layout, { id: widget.id, width: widget.width.default }]
}

/** What the Add list offers: accessible widgets not on the page, in registry order. */
export function addable(layout: HomeLayoutEntry[], widgets: LayoutWidget[], canSee: (id: string) => boolean): LayoutWidget[] {
  return widgets.filter((x) => canSee(x.id) && !layout.some((e) => e.id === x.id))
}

/** Which column group a width belongs to: narrow cards ride the rail, wide ones the main lane. */
export type Lane = 'main' | 'rail' | 'full'

export function laneOf(width: HomeWidth): Lane {
  return width === 1 ? 'rail' : width === 2 ? 'main' : 'full'
}

export type Segment =
  | { kind: 'full'; entry: HomeLayoutEntry }
  | { kind: 'run'; entries: HomeLayoutEntry[] }

/** The visible list cut at its full-width cards: each full card is its own segment, each
 * maximal run of the rest another. Pass the visible list, so a hidden full card cuts nothing. */
export function segments(visible: HomeLayoutEntry[]): Segment[] {
  const out: Segment[] = []
  for (const entry of visible) {
    if (entry.width === 3) {
      out.push({ kind: 'full', entry })
      continue
    }
    const last = out[out.length - 1]
    if (last && last.kind === 'run') last.entries.push(entry)
    else out.push({ kind: 'run', entries: [entry] })
  }
  return out
}

export type DrawItem =
  | { kind: 'card'; entry: HomeLayoutEntry; span: HomeWidth }
  | { kind: 'lanes'; key: string; main: HomeLayoutEntry[]; rail: HomeLayoutEntry[] }

/** What the page draws. Without lanes every entry is a card of its own width (today's grid);
 * with them a run holding both lanes is one lane pair, and a run of one lane stacks full width
 * (wide) or in the grid (narrow). A lane pair is keyed by the visible full card above it (`top`
 * when none is), so adding, removing or resizing a card elsewhere never remounts it. */
export function drawItems(visible: HomeLayoutEntry[], lanes: boolean): DrawItem[] {
  if (!lanes) return visible.map((entry) => ({ kind: 'card', entry, span: entry.width }))
  let above: string | null = null
  return segments(visible).flatMap((seg): DrawItem[] => {
    if (seg.kind === 'full') {
      above = seg.entry.id
      return [{ kind: 'card', entry: seg.entry, span: 3 }]
    }
    const main = seg.entries.filter((x) => laneOf(x.width) === 'main')
    const rail = seg.entries.filter((x) => laneOf(x.width) === 'rail')
    if (main.length > 0 && rail.length > 0) return [{ kind: 'lanes', key: above ? `after-${above}` : 'top', main, rail }]
    return seg.entries.map((entry) => ({ kind: 'card', entry, span: main.length > 0 ? 3 : 1 }))
  })
}

/** The card a Move arrow swaps with: the adjacent one in list order, or with lanes the next
 * one in the mover's own lane (or a full card, which stops it). A full mover meets its neighbor. */
export function laneNeighbour(visible: HomeLayoutEntry[], id: string, direction: -1 | 1, lanes: boolean): HomeLayoutEntry | null {
  const from = visible.findIndex((x) => x.id === id)
  if (from < 0) return null
  const lane = laneOf(visible[from].width)
  for (let i = from + direction; i >= 0 && i < visible.length; i += direction) {
    const candidate = visible[i]
    if (!lanes || lane === 'full') return candidate
    const there = laneOf(candidate.width)
    if (there === lane || there === 'full') return candidate
  }
  return null
}

/** Whether a drag may land on a card: anywhere below the list frame; with lanes within the
 * same lane, and a full card on or from any card. */
export function canDrop(visible: HomeLayoutEntry[], activeId: string, overId: string, lanes: boolean): boolean {
  if (!lanes) return true
  const active = visible.find((x) => x.id === activeId)
  const over = visible.find((x) => x.id === overId)
  if (!active || !over) return false
  const a = laneOf(active.width)
  const o = laneOf(over.width)
  return a === 'full' || o === 'full' || a === o
}

/** The sortable ids in the order the lanes are drawn (a run's main lane, then its rail), each
 * keyed for the lanes drag context, equal keys always contiguous: `<s>:full` for a stretch of
 * adjacent full cards (`s` its first segment), `<s>:main` / `<s>:rail` for a run with a wide card
 * (each a vertical stack), and `<s>:grid` for a run of narrow cards only, drawn side by side. */
export function dragOrder(visible: HomeLayoutEntry[]): { ids: string[]; keys: string[] } {
  const ids: string[] = []
  const keys: string[] = []
  let stretch: string | null = null
  segments(visible).forEach((seg, s) => {
    if (seg.kind === 'full') {
      stretch ??= `${s}:full`
      ids.push(seg.entry.id)
      keys.push(stretch)
      return
    }
    stretch = null
    if (!seg.entries.some((x) => laneOf(x.width) === 'main')) {
      for (const entry of seg.entries) {
        ids.push(entry.id)
        keys.push(`${s}:grid`)
      }
      return
    }
    for (const lane of ['main', 'rail'] as const) {
      for (const entry of seg.entries.filter((x) => laneOf(x.width) === lane)) {
        ids.push(entry.id)
        keys.push(`${s}:${lane}`)
      }
    }
  })
  return { ids, keys }
}
